import React, { Component, ErrorInfo, ReactNode } from 'react';
import { HiExclamationTriangle, HiArrowPath } from 'react-icons/hi2';
import { logger } from '../../utils/logger';

// Support email - from env or default
const SUPPORT_EMAIL = import.meta.env.REACT_APP_SUPPORT_EMAIL || 'support@voicebrain.ai';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary for V2 UI
 *
 * Catches JavaScript errors in child components and displays
 * a fallback UI instead of crashing the whole app.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });

    // Log error details
    logger.error('[ErrorBoundary] Caught error:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack
    });

    // TODO: Could send to error tracking service (Sentry, etc.)
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-vb-bg-primary dark:bg-vb-bg-primary-dark p-8">
          <div className="max-w-md w-full text-center">
            {/* Error Icon */}
            <div className="mx-auto w-16 h-16 rounded-full bg-vb-danger/10 flex items-center justify-center mb-6">
              <HiExclamationTriangle className="w-8 h-8 text-vb-danger" />
            </div>

            {/* Title */}
            <h1 className="text-2xl font-bold text-vb-text-primary dark:text-vb-text-primary-dark mb-2">
              Something went wrong
            </h1>

            {/* Description */}
            <p className="text-vb-text-secondary dark:text-vb-text-secondary-dark mb-6">
              An unexpected error occurred. Our team has been notified.
            </p>

            {/* Error details (collapsible in dev) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="text-left mb-6 p-4 bg-vb-surface dark:bg-vb-surface-dark rounded-lg border border-vb-border dark:border-vb-border-dark">
                <summary className="cursor-pointer text-sm font-medium text-vb-text-primary dark:text-vb-text-primary-dark">
                  Error Details
                </summary>
                <pre className="mt-2 text-xs text-vb-danger overflow-auto max-h-48 p-2 bg-vb-bg-secondary dark:bg-vb-bg-secondary-dark rounded">
                  {this.state.error.message}
                  {'\n\n'}
                  {this.state.error.stack}
                </pre>
              </details>
            )}

            {/* Actions */}
            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleReset}
                className="px-4 py-2 text-sm font-medium text-vb-text-primary dark:text-vb-text-primary-dark bg-vb-surface dark:bg-vb-surface-dark border border-vb-border dark:border-vb-border-dark rounded-lg hover:bg-vb-surface-hover dark:hover:bg-vb-surface-hover-dark transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={this.handleReload}
                className="px-4 py-2 text-sm font-medium text-white bg-vb-primary hover:bg-vb-primary-dark rounded-lg transition-colors flex items-center gap-2"
              >
                <HiArrowPath className="w-4 h-4" />
                Reload Page
              </button>
            </div>

            {/* Support link */}
            <p className="mt-6 text-sm text-vb-text-secondary dark:text-vb-text-secondary-dark">
              If the problem persists,{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-vb-primary hover:underline">
                contact support
              </a>
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
